import SEOHead from '@/components/feature/SEOHead';
import Breadcrumb from '@/components/feature/Breadcrumb';


const aboutSchema = {
  '@context': 'https://schema.org',
  '@type': 'AboutPage',
  name: '关于天堂影院',
  description: '天堂影院是一个专注于高清影视在线播放的平台，主打高清不缓冲流畅在线观看，无广告影视极速播放，蓝光画质免费在线看大片。',
  url: 'https://tiantangcinema.com/about',
  isPartOf: {
    '@type': 'WebSite',
    name: '天堂影院',
    url: 'https://tiantangcinema.com',
  },
};

const faqSchema = {
  '@context': 'https://schema.org',
  '@type': 'FAQPage',
  mainEntity: [
    {
      '@type': 'Question',
      name: '天堂影院需要付费吗？',
      acceptedAnswer: {
        '@type': 'Answer',
        text: '天堂影院完全免费，所有高清影视资源均可免费在线观看，无需充值会员，无需任何费用。我们致力于为广大影迷提供最优质的免费观影体验。',
      },
    },
    {
      '@type': 'Question',
      name: '播放会卡顿吗？如何解决？',
      acceptedAnswer: {
        '@type': 'Answer',
        text: '天堂影院采用全球CDN加速技术，确保观影流畅不卡顿。如果您遇到卡顿情况，建议检查网络连接，或尝试切换清晰度。我们持续优化服务器性能，保障稳定播放。',
      },
    },
    {
      '@type': 'Question',
      name: '支持哪些设备观看？',
      acceptedAnswer: {
        '@type': 'Answer',
        text: '天堂影院支持电脑、平板、手机等多种设备在线观看。无论您是使用Windows、Mac、iOS还是Android系统，都可以流畅访问天堂影院，随时随享高清影视。',
      },
    },
    {
      '@type': 'Question',
      name: '画质如何？支持蓝光吗？',
      acceptedAnswer: {
        '@type': 'Answer',
        text: '天堂影院提供多种清晰度选择，包括高清、超清和蓝光画质。您可以根据网络状况和设备性能选择合适的画质，享受最佳的视觉效果。',
      },
    },
    {
      '@type': 'Question',
      name: '影片资源更新频率如何？',
      acceptedAnswer: {
        '@type': 'Answer',
        text: '天堂影院每天更新最新影片资源，涵盖国内外热门电影、经典老片、最新上映的大片等。我们会第一时间同步更新，让您不错过任何精彩内容。',
      },
    },
  ],
};

export default function AboutPage() {
  const stats = [
    { value: '1000+', label: '高清影片' },
    { value: '50万+', label: '日活用户' },
    { value: '99.9%', label: '播放成功率' },
    { value: '10年', label: '品牌历史' },
  ];

  const team = [
    { name: '张明远', role: '创始人 & CEO', desc: '资深电影爱好者，15年影视行业从业经验，致力于为影迷打造最佳观影平台。' },
    { name: '林小雅', role: '技术总监', desc: '前硅谷工程师，专注于流媒体技术与CDN加速，确保每一位用户都能流畅观影。' },
    { name: '王浩然', role: '内容总监', desc: '资深影评人，拥有电影学硕士学位，负责内容策划与影片审核。' },
  ];

  return (
    <>
      <SEOHead
        title="关于我们 - 品牌介绍 口碑 联系方式"
        description="天堂影院是一个专注于高清影视在线播放的平台，主打高清不缓冲流畅在线观看，无广告影视极速播放，蓝光画质免费在线看大片。了解天堂影院的品牌故事、口碑与联系方式。"
        keywords="天堂影院,关于我们,品牌介绍,联系方式,高清在线播放,免费电影"
        canonicalUrl="https://tiantangcinema.com/about"
        schema={[aboutSchema, faqSchema]}
      />

      <main className="min-h-screen pt-16">
        <div className="px-4 md:px-6 py-6 md:py-8">
          <div className="max-w-5xl mx-auto">
            <Breadcrumb items={[{ label: '关于我们' }]} />

            {/* Hero */}
            <section className="text-center mb-12 md:mb-16 py-8 md:py-12">
              <div className="w-16 h-16 md:w-20 md:h-20 flex items-center justify-center mx-auto mb-5 rounded-full bg-primary-100/70">
                <i className="ri-movie-2-fill text-primary-500 text-3xl md:text-4xl"></i>
              </div>
              <h1 className="text-2xl md:text-4xl font-bold font-heading text-foreground-50 mb-3">
                关于天堂影院
              </h1>
              <p className="text-sm md:text-base text-foreground-600 max-w-2xl mx-auto leading-relaxed">
                天堂影院成立于2016年，是一个专注于高清影视在线播放的平台。我们主打&quot;高清不缓冲、流畅在线观看&quot;，以无广告影视、极速播放、蓝光画质为核心竞争力，致力于为全球华语影迷提供最优质的免费在线观影体验。
              </p>
            </section>

            {/* Stats */}
            <section className="mb-12 md:mb-16">
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 md:gap-5">
                {stats.map((stat) => (
                  <div key={stat.label} className="bg-background-100 rounded-lg p-5 md:p-6 text-center border border-background-300/40">
                    <p className="text-2xl md:text-3xl font-bold font-heading text-accent-500 mb-1">{stat.value}</p>
                    <p className="text-xs md:text-sm text-foreground-600">{stat.label}</p>
                  </div>
                ))}
              </div>
            </section>

            {/* Story */}
            <section className="mb-12 md:mb-16">
              <h2 className="text-xl md:text-2xl font-bold font-heading text-foreground-50 mb-4 md:mb-5">品牌故事</h2>
              <div className="bg-background-100 rounded-lg p-5 md:p-7 border border-background-300/40">
                <p className="text-sm md:text-base text-foreground-400 leading-relaxed mb-4">
                  天堂影院的诞生，源于创始人对电影的热爱和对优质观影体验的追求。在创办天堂影院之前，创始人张明远发现市面上的在线观影平台普遍存在广告过多、缓冲频繁、画质低劣等问题，严重影响了用户的观影体验。
                </p>
                <p className="text-sm md:text-base text-foreground-400 leading-relaxed mb-4">
                  于是，他决定打造一个真正以用户体验为核心的影视平台——天堂影院。经过近一年的技术研发与内容积累，天堂影院于2016年正式上线。平台采用全球领先的CDN加速技术和自适应码率算法，确保不同网络环境下的用户都能获得流畅的观影体验。
                </p>
                <p className="text-sm md:text-base text-foreground-400 leading-relaxed">
                  十年来，天堂影院始终坚持&quot;免费、高清、流畅、无广告&quot;的核心原则，从最初只有几百部影片的小平台，发展到现在拥有超过1000部高清影片的综合性影视平台，日活跃用户突破50万，成为华语地区最受欢迎的免费在线观影平台之一。
                </p>
              </div>
            </section>

            {/* Mission */}
            <section className="mb-12 md:mb-16">
              <h2 className="text-xl md:text-2xl font-bold font-heading text-foreground-50 mb-4 md:mb-5">品牌使命</h2>
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 md:gap-5">
                <div className="bg-background-100 rounded-lg p-5 md:p-6 border border-background-300/40">
                  <div className="w-10 h-10 flex items-center justify-center rounded-full bg-primary-100/70 mb-3">
                    <i className="ri-eye-fill text-primary-500 text-lg"></i>
                  </div>
                  <h3 className="text-sm md:text-base font-semibold text-foreground-200 mb-2">极致画质</h3>
                  <p className="text-xs md:text-sm text-foreground-600 leading-relaxed">
                    持续升级播放技术，提供4K蓝光级别的画质体验，让每一位用户都能享受影院级的视觉盛宴。
                  </p>
                </div>
                <div className="bg-background-100 rounded-lg p-5 md:p-6 border border-background-300/40">
                  <div className="w-10 h-10 flex items-center justify-center rounded-full bg-accent-100/70 mb-3">
                    <i className="ri-shield-flash-fill text-accent-500 text-lg"></i>
                  </div>
                  <h3 className="text-sm md:text-base font-semibold text-foreground-200 mb-2">流畅体验</h3>
                  <p className="text-xs md:text-sm text-foreground-600 leading-relaxed">
                    通过全球CDN加速和智能缓存技术，实现秒开不缓冲的极致流畅播放体验。
                  </p>
                </div>
                <div className="bg-background-100 rounded-lg p-5 md:p-6 border border-background-300/40">
                  <div className="w-10 h-10 flex items-center justify-center rounded-full bg-secondary-100/70 mb-3">
                    <i className="ri-user-heart-fill text-secondary-500 text-lg"></i>
                  </div>
                  <h3 className="text-sm md:text-base font-semibold text-foreground-200 mb-2">用户至上</h3>
                  <p className="text-xs md:text-sm text-foreground-600 leading-relaxed">
                    始终以用户需求为导向，倾听每一位用户的声音，不断优化产品和服务体验。
                  </p>
                </div>
              </div>
            </section>

            {/* Team */}
            <section className="mb-12 md:mb-16">
              <h2 className="text-xl md:text-2xl font-bold font-heading text-foreground-50 mb-4 md:mb-5">核心团队</h2>
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 md:gap-5">
                {team.map((member) => (
                  <div key={member.name} className="bg-background-100 rounded-lg p-5 md:p-6 border border-background-300/40 text-center">
                    <div className="w-14 h-14 md:w-16 md:h-16 rounded-full bg-primary-100/70 mx-auto mb-3 flex items-center justify-center">
                      <i className="ri-user-3-fill text-primary-500 text-xl md:text-2xl"></i>
                    </div>
                    <h3 className="text-sm md:text-base font-semibold text-foreground-200 mb-1">{member.name}</h3>
                    <p className="text-xs text-primary-500 font-medium mb-2">{member.role}</p>
                    <p className="text-xs md:text-sm text-foreground-600 leading-relaxed">{member.desc}</p>
                  </div>
                ))}
              </div>
            </section>

            {/* FAQ */}
            <section className="mb-12 md:mb-16">
              <h2 className="text-xl md:text-2xl font-bold font-heading text-foreground-50 mb-4 md:mb-5">常见问题</h2>
              <div className="space-y-3">
                {faqSchema.mainEntity.map((faq, index) => (
                  <details key={index} className="bg-background-100 rounded-lg border border-background-300/40 group">
                    <summary className="p-4 md:p-5 cursor-pointer text-sm md:text-base font-medium text-foreground-300 flex items-center justify-between">
                      {faq.name}
                      <div className="w-5 h-5 flex items-center justify-center">
                        <i className="ri-arrow-down-s-line text-foreground-600 group-open:rotate-180 transition-transform duration-200"></i>
                      </div>
                    </summary>
                    <div className="px-4 md:px-5 pb-4 md:pb-5 text-sm text-foreground-600 leading-relaxed">
                      {faq.acceptedAnswer.text}
                    </div>
                  </details>
                ))}
              </div>
            </section>

            {/* Contact */}
            <section className="mb-8">
              <h2 className="text-xl md:text-2xl font-bold font-heading text-foreground-50 mb-4 md:mb-5">联系我们</h2>
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 md:gap-8">
                <div className="bg-background-100 rounded-lg p-5 md:p-7 border border-background-300/40">
                  <h3 className="text-base font-semibold text-foreground-200 mb-4">联系方式</h3>
                  <div className="space-y-3">
                    <div className="flex items-center gap-3">
                      <div className="w-8 h-8 flex items-center justify-center rounded-full bg-primary-100/70">
                        <i className="ri-mail-line text-primary-500"></i>
                      </div>
                      <span className="text-sm text-foreground-400">contact@tiantangcinema.com</span>
                    </div>
                    <div className="flex items-center gap-3">
                      <div className="w-8 h-8 flex items-center justify-center rounded-full bg-accent-100/70">
                        <i className="ri-customer-service-2-line text-accent-500"></i>
                      </div>
                      <span className="text-sm text-foreground-400">在线客服 7×24小时</span>
                    </div>
                    <div className="flex items-center gap-3">
                      <div className="w-8 h-8 flex items-center justify-center rounded-full bg-secondary-100/70">
                        <i className="ri-map-pin-line text-secondary-500"></i>
                      </div>
                      <span className="text-sm text-foreground-400">中国北京市朝阳区光华路88号</span>
                    </div>
                  </div>
                </div>
                <div className="bg-background-100 rounded-lg p-5 md:p-7 border border-background-300/40">
                  <h3 className="text-base font-semibold text-foreground-200 mb-4">发送消息</h3>
                  <form
                    id="contact-form"
                    data-readdy-form
                    action="https://readdy.ai/api/form/d9n3g3e8mbnljin59td0"
                    method="POST"
                    className="space-y-3"
                  >
                    <input
                      type="text"
                      name="name"
                      placeholder="您的姓名"
                      required
                      className="w-full px-4 py-2.5 bg-background-50 border border-background-300/60 rounded-md text-sm text-foreground-200 placeholder-secondary-400 outline-none focus:border-primary-500/60 transition-colors duration-200"
                    />
                    <input
                      type="email"
                      name="email"
                      placeholder="您的邮箱"
                      required
                      className="w-full px-4 py-2.5 bg-background-50 border border-background-300/60 rounded-md text-sm text-foreground-200 placeholder-secondary-400 outline-none focus:border-primary-500/60 transition-colors duration-200"
                    />
                    <textarea
                      name="message"
                      placeholder="请输入您的留言内容..."
                      rows={3}
                      maxLength={500}
                      required
                      className="w-full px-4 py-2.5 bg-background-50 border border-background-300/60 rounded-md text-sm text-foreground-200 placeholder-secondary-400 outline-none focus:border-primary-500/60 transition-colors duration-200 resize-none"
                    ></textarea>
                    <div className="form-phone-alt-wrap">
                      <input
                        type="text"
                        name="mobile_alt"
                        tabIndex={-1}
                        autoComplete="off"
                        aria-hidden="true"
                        readOnly
                      />
                    </div>
                    <button
                      type="submit"
                      className="w-full bg-primary-500 hover:bg-primary-600 text-background-50 py-2.5 rounded-md text-sm font-semibold transition-colors duration-200 cursor-pointer whitespace-nowrap"
                    >
                      发送消息
                    </button>
                  </form>
                </div>
              </div>
            </section>
          </div>
        </div>
      </main>
    </>
  );
}